/**
 * BadgeGrid — Displays a user's earned badges in a grid layout.
 * Used on the profile screen and public profile views.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, Pressable } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { fetchBadges, BADGE_DEFS, type Badge } from '@/lib/badges';

interface BadgeGridProps {
  profileId: string;
  /** Max badges to show before "See All" */
  limit?: number;
}

export default function BadgeGrid({ profileId, limit = 12 }: BadgeGridProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    fetchBadges(profileId).then(b => {
      setBadges(b);
      setLoading(false);
    });
  }, [profileId]);

  if (loading) return null;

  const earnedKeys = new Set(badges.map(b => b.badge_key));
  // Empty state nudge rendered below header when no badges earned
  const earnedMap = new Map(badges.map(b => [b.badge_key, b]));

  // Build full list: all defs, earned first then unearned
  const allBadges = BADGE_DEFS.map(def => ({
    def,
    earned: earnedMap.get(def.key) ?? null,
  }));

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
          BADGES
        </Text>
        <Text style={[styles.count, { color: c.textSecondary }]}>
          {badges.length} / {BADGE_DEFS.length}
        </Text>
      </View>

      {badges.length === 0 && (
        <Text style={[styles.emptyHint, { color: c.textSecondary }]}>
          No badges yet. Log your first workout to earn Iron Initiate.
        </Text>
      )}

      <View style={styles.grid}>
        {allBadges.map(({ def, earned }) => (
          <TouchableOpacity
            key={def.key}
            style={[
              styles.badgeCell,
              {
                backgroundColor: earned ? c.surface : c.surface,
                borderColor: earned ? c.accent + '30' : c.border,
                opacity: earned ? 1 : 0.45,
              },
            ]}
            onPress={() => earned && setSelectedBadge(earned)}
            activeOpacity={earned ? 0.7 : 1}
          >
            <Text style={styles.emoji}>{earned ? def.emoji : '🔒'}</Text>
            <Text
              style={[
                styles.label,
                {
                  color: earned ? c.text : c.textSecondary,
                  fontFamily: TYPO.body.semibold,
                },
              ]}
              numberOfLines={1}
            >
              {def.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Badge detail modal */}
      <Modal visible={!!selectedBadge} transparent animationType="fade" onRequestClose={() => setSelectedBadge(null)}>
        <Pressable style={styles.overlay} onPress={() => setSelectedBadge(null)}>
          <View style={[styles.modal, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={styles.modalEmoji}>{selectedBadge?.badge_emoji}</Text>
            <Text style={[styles.modalTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>
              {selectedBadge?.badge_label}
            </Text>
            <Text style={[styles.modalDesc, { color: c.textSecondary }]}>
              {selectedBadge?.badge_description}
            </Text>
            <Text style={[styles.modalDate, { color: c.textTertiary }]}>
              Earned {selectedBadge ? new Date(selectedBadge.awarded_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
            </Text>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: SPACE.sm },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 11, letterSpacing: 1.5 },
  count: { fontSize: 12 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACE.xs },
  badgeCell: {
    width: '30%',
    flexGrow: 1,
    alignItems: 'center',
    paddingVertical: SPACE.sm,
    paddingHorizontal: SPACE.xs,
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    gap: 4,
  },
  emoji: { fontSize: 24 },
  label: { fontSize: 11, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5 },
  seeAll: { fontSize: 12, textAlign: 'center', marginTop: SPACE.xs, letterSpacing: 0.5 },
  emptyHint: { fontSize: 13, textAlign: 'center', paddingVertical: SPACE.sm },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' },
  modal: { width: 280, padding: 24, borderRadius: RADIUS.xs, borderWidth: 1, alignItems: 'center', gap: SPACE.sm },
  modalEmoji: { fontSize: 48 },
  modalTitle: { fontSize: FONT.lg },
  modalDesc: { fontSize: FONT.sm, textAlign: 'center', lineHeight: 20 },
  modalDate: { fontSize: 11, marginTop: SPACE.xs },
});
